/**
 * WP-V2-TW — TownScene: żywa plansza 3 dzielnic (3840×720).
 * Konsumuje: AnimatedDogAvatar, ObjectLayer, WorldCamera, WalkController, Minimap, EdgeTransition.
 */
import Phaser from 'phaser';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { ensureHudOverlay } from '../../core/HudScene.js';
import { ensureCompanionOverlay } from '../../companion/index.js';
import { ensureQuestOverlay, QuestLayer } from '../../quests/index.js';
import {
  setupWorld,
  follow,
  Minimap,
  EdgeTransition,
} from '../../core/world/index.js';
import type { MinimapLandmark } from '../../core/world/types.js';
import { ObjectLayer } from '../../interact/ObjectLayer.js';
import type { InteractEvent } from '../../interact/types.js';
import { listHouses, requestResidency } from '../house/houseApi.js';
import {
  estateSlotsFromHouses,
  type EstateSlotVM,
} from './estateModel.js';
import {
  TOWN_BUILDING_HOTSPOTS,
  TOWN_ESTATE_PLOTS,
  TOWN_WORLD_W,
  TOWN_WORLD_H,
  TOWN_DISTRICTS,
  TOWN_GARDEN_GATE,
  TOWN_PLAYER_START,
  districtAtX,
  type TownHotspot,
  attachBuildingHotspotNarration,
} from './hotspots.js';
import { playFx } from '../../interact/fx.js';
import { buildTownColliders } from './colliders.js';
import {
  TOWN_OBJECT_DEFS,
  TOWN_OBJECT_PLACEMENTS,
} from './objects.js';
import { LocalDogs } from './LocalDogs.js';
import { PlayerPawn } from './PlayerPawn.js';
import { MisiaLola } from './MisiaLola.js';
import { DragonAmbient } from './DragonAmbient.js';
import { STREET_CAT_SPAWNS } from './hotspots.js';
import { StreetCats } from './StreetCats.js';
import {
  MICRO_INTERACTIONS,
  microInteractionForScene,
  type TownMicroAction,
  type TownMicroInteractionPlan,
} from './microInteractions.js';
import {
  TOWN_GROUND_Y,
  TOWN_WALK_BAND,
  buildingPlacement,
  depthForGroundY,
  doorApproachPoint,
} from './townLayout.js';

export class TownScene extends BaseScene {
  static readonly KEY = 'Town';

  private player: PlayerPawn | null = null;
  private dogs: LocalDogs | null = null;
  private friends: MisiaLola | null = null;
  private dragon: DragonAmbient | null = null;
  private streetCats: StreetCats | null = null;
  private objectLayer: ObjectLayer | null = null;
  private minimap: Minimap | null = null;
  private edges: EdgeTransition | null = null;
  private estateLayer: Phaser.GameObjects.Container | null = null;
  private toastText: Phaser.GameObjects.Text | null = null;
  private enterPrompt: Phaser.GameObjects.Container | null = null;
  private buildings: {
    key: string;
    x: number;
    y: number;
    glow: Phaser.GameObjects.Arc;
    hotspot: TownHotspot;
  }[] = [];
  private nearKey: string | null = null;
  private worldHandle: { destroy(): void } | null = null;
  private unsubObjects: (() => void) | null = null;
  private navigating = false;

  constructor() {
    super(TownScene.KEY);
  }

  preload(): void {
    // Czyste ilustracje dzielnic (W9-A / art W9-D). Brak pliku → textures.exists
    // false → proceduralny fallback w drawWorldBackground (bez twardego faila).
    for (const id of ['bg_town_osiedle', 'bg_town_rynek', 'bg_town_pole'] as const) {
      if (!this.textures.exists(id)) {
        this.load.image(id, `/assets/real/${id}.png`);
      }
    }
    // cichy fallback gdy brak assetu dzielnicy (404 nie psuje sceny)
    this.load.once('loaderror', (file: { key?: string }) => {
      const key = file?.key ?? '';
      if (key.startsWith('bg_town_')) {
        // no-op — drawWorldBackground użyje grafiki proceduralnej
      }
    });
    if (!this.textures.exists('ph_dog')) {
      this.load.image('ph_dog', '/assets/placeholder/dog.png');
    }
    // DragonAmbient ma dopracowany fallback proceduralny; nie żądaj brakującego
    // dragon_day.png, bo Phaser raportuje wtedy błąd przy każdym wejściu do Town.
    for (const o of [
      'house',
      'wardrobe',
      'shop',
      'cafe',
      'school',
      'park',
      'pool',
      'stable',
      'garden',
      'icecream',
      'vet',
    ]) {
      const key = `bldg_${o}`;
      if (!this.textures.exists(key)) {
        this.load.image(key, `/assets/real/bldg_${o}.png`);
      }
    }
  }

  create(): void {
    ensureHudOverlay(this.game);
    ensureCompanionOverlay(this.game);
    ensureQuestOverlay(this.game);
    // Oferta misji od kotka gdy dostępna (brak aktywnej / oferty).
    // delay: QuestLayer.create musi zdążyć wystartować po ensureQuestOverlay.
    this.time.delayedCall(120, () => this.offerKotekQuestIfAvailable());

    this.worldHandle = setupWorld(this, {
      worldW: TOWN_WORLD_W,
      worldH: TOWN_WORLD_H,
      bg: 0xb8e6d0,
    });

    this.drawWorldBackground();
    this.drawDistrictLabels();
    this.drawGardenGate();

    const colliders = buildTownColliders();
    this.drawColliderHints(colliders);

    this.spawnBuildingHotspots();
    this.spawnEstate();

    this.player = new PlayerPawn(this, colliders);

    this.objectLayer = new ObjectLayer({
      scene: this,
      defs: [...TOWN_OBJECT_DEFS],
      target: this.player,
      interactRange: 100,
      groundBounds: {
        minX: 60,
        maxX: TOWN_WORLD_W - 60,
        minY: TOWN_WALK_BAND.minY,
        maxY: TOWN_WORLD_H - 60,
      },
    });
    this.objectLayer.spawn([...TOWN_OBJECT_PLACEMENTS]);
    this.unsubObjects = this.objectLayer.on((evt) => this.onObjectEvent(evt));

    this.dogs = new LocalDogs(this);
    this.dogs.spawn();

    this.friends = new MisiaLola(this);
    this.friends.spawn();

    this.streetCats = new StreetCats(this);
    this.streetCats.spawn();

    this.dragon = new DragonAmbient(this);
    this.dragon.start();

    follow(this, this.player.container, {
      lerp: 0.12,
      deadzone: { width: 120, height: 80 },
    });
    // start scroll na Rynku
    this.cameras.main.centerOn(TOWN_PLAYER_START.x, TOWN_PLAYER_START.y);

    this.minimap = new Minimap(this, {
      worldW: TOWN_WORLD_W,
      worldH: TOWN_WORLD_H,
      landmarks: this.buildMinimapLandmarks(),
      visible: true,
    });

    this.edges = new EdgeTransition({
      worldW: TOWN_WORLD_W,
      worldH: TOWN_WORLD_H,
      band: 56,
      onEnter: (z) => {
        if (z.id === 'edge-e' || z.targetKey === 'Garden') {
          this.showToast('Brama Ogrodu Marzeń → 🌸');
        } else if (z.id === 'edge-w') {
          this.showToast('Osiedle ← 🏠');
        }
      },
    });

    this.drawHudChrome();

    this.events.once('shutdown', () => this.teardown());
    this.events.once('destroy', () => this.teardown());
  }

  update(_t: number, dtMs: number): void {
    const dtSec = Math.min(0.05, dtMs / 1000);
    this.player?.update(dtSec);
    this.objectLayer?.update(dtSec);
    this.dogs?.update(dtSec);
    this.friends?.update(dtSec, this.player?.position ?? null);
    this.streetCats?.update(dtSec, this.player?.position ?? null);
    this.updateProximity();

    const pos = this.player?.position;
    if (pos) {
      this.minimap?.setPlayerPosition(pos.x, pos.y);
      this.edges?.update(pos.x, pos.y);
      this.registry.set('companionFollow', { x: pos.x, y: pos.y });
      this.registry.set('townDistrict', districtAtX(pos.x));
    }
  }

  private drawWorldBackground(): void {
    this.cameras.main.setBackgroundColor(0xb8e6d0);

    // 3 szerokie dzielnice. Preferuj czyste ilustracje bg_town_<id> (W9-D art);
    // inaczej proceduralne niebo + trawa na TOWN_GROUND_Y.
    const horizon = TOWN_GROUND_Y;
    let anyDistrictBg = false;
    for (const d of TOWN_DISTRICTS) {
      const segKey =
        d.id === 'osiedle'
          ? 'bg_town_osiedle'
          : d.id === 'rynek'
            ? 'bg_town_rynek'
            : 'bg_town_pole';
      const cx = d.cx;
      const segW = d.x1 - d.x0;
      const x0 = d.x0;
      if (this.textures.exists(segKey)) {
        anyDistrictBg = true;
        this.add
          .image(cx, TOWN_WORLD_H / 2, segKey)
          .setDisplaySize(segW, TOWN_WORLD_H)
          .setDepth(-1000);
        continue;
      }

      const g = this.add.graphics().setDepth(-1000);
      // niebo — miękki błękitny gradient
      g.fillGradientStyle(0xbfe6ff, 0xbfe6ff, 0xeaf7ff, 0xeaf7ff, 1);
      g.fillRect(x0, 0, segW, horizon);
      // delikatny akcent dzielnicy u góry (odróżnia osiedle/rynek/pole)
      g.fillStyle(d.color, 0.22);
      g.fillRect(x0, 0, segW, 96);
      // ziemia — trawa
      g.fillStyle(0x9ed99b, 1);
      g.fillRect(x0, horizon, segW, TOWN_WORLD_H - horizon);

      // miękkie wzgórza na horyzoncie
      const hillColors = [0x8ccf8a, 0x7cc47b, 0xa7dda3];
      let hs = d.id === 'osiedle' ? 3 : d.id === 'rynek' ? 11 : 23;
      const hrand = (): number => {
        hs = (hs * 9301 + 49297) % 233280;
        return hs / 233280;
      };
      for (let hx = x0 + 60; hx < d.x1 - 60; hx += 300) {
        const rw = 260 + hrand() * 180;
        const rh = 120 + hrand() * 70;
        g.fillStyle(hillColors[Math.floor(hrand() * hillColors.length)]!, 0.9);
        g.fillEllipse(hx, horizon + 8, rw, rh);
      }
    }

    // Ambient (drzewa/krzaki) tylko gdy brak teł dzielnic — inaczej dublują art.
    if (!anyDistrictBg) {
      this.drawAmbientScenery();
    }
  }

  /**
   * Dodatkowy ambient (drzewa w tle, krzaki/kwiaty na pierwszym planie, pas
   * ścieżki) rozłożony po CAŁEJ szerokości świata 6400 px — żeby powiększony
   * świat nie był pusty między budynkami. Czysto wizualne (bez kolizji),
   * pomijają x blisko środków budynków, żeby nie zasłaniać wejść.
   */
  private drawAmbientScenery(): void {
    // pas ścieżki pod stopami — sugeruje ciągły trakt przez całe miasteczko
    this.add
      .rectangle(TOWN_WORLD_W / 2, 612, TOWN_WORLD_W, 70, 0xead9bd, 0.32)
      .setDepth(-600);

    const treeColors = [0x6fae6f, 0x7fbf7f, 0x5fa05f, 0x8cc98c];
    const bushColors = [0x7fbf7f, 0x6fae6f, 0x9ed19e, 0xffd6e6];
    let seed = 17;
    const rand = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    const nearBuilding = (x: number, margin: number): boolean =>
      TOWN_BUILDING_HOTSPOTS.some(
        (h) => Math.abs(h.x - x) < (h.width ?? 150) / 2 + margin,
      );

    // drzewa tylko na trawie (y ≥ TOWN_GROUND_Y) — nie w niebie
    for (let x = 140; x < TOWN_WORLD_W - 140; x += 260) {
      if (nearBuilding(x, 45)) continue;
      const y = TOWN_GROUND_Y + 24 + rand() * 40;
      const scale = 0.85 + rand() * 0.35;
      const color = treeColors[Math.floor(rand() * treeColors.length)]!;
      this.drawAmbientTree(x, y, scale, color);
    }

    // krzaki / kwiatki na pierwszym planie wzdłuż ścieżki, gęściej
    for (let x = 170; x < TOWN_WORLD_W - 170; x += 190) {
      if (nearBuilding(x, 30)) continue;
      const y = Math.min(668 + rand() * 14, TOWN_WALK_BAND.maxY + 20);
      const color = bushColors[Math.floor(rand() * bushColors.length)]!;
      this.drawAmbientBush(x, y, color);
    }
  }

  /**
   * Realistyczne drzewo Toca (pień + warstwy korony + cień).
   * Bez „głupich rzeczy” na gałęziach — czysta natura.
   */
  private drawAmbientTree(x: number, y: number, scale: number, color: number): void {
    const trunkH = 36 * scale;
    const crownY = y - trunkH * 0.55;
    // cień u podstawy
    this.add
      .ellipse(x, y + 10 * scale, 28 * scale, 10 * scale, 0x000000, 0.1)
      .setDepth(-502);
    // pień
    this.add
      .rectangle(x, y - trunkH * 0.15, 10 * scale, trunkH, 0x8d6e63)
      .setDepth(-501);
    // korona — 3 owalne warstwy (naturalna sylwetka, nie jeden „balon”)
    const dark = Phaser.Display.Color.IntegerToColor(color).darken(18).color;
    const light = Phaser.Display.Color.IntegerToColor(color).lighten(12).color;
    this.add.ellipse(x - 6 * scale, crownY + 4 * scale, 34 * scale, 28 * scale, dark, 0.95).setDepth(-500);
    this.add.ellipse(x + 8 * scale, crownY + 2 * scale, 30 * scale, 26 * scale, color, 0.95).setDepth(-499);
    this.add.ellipse(x, crownY - 10 * scale, 28 * scale, 24 * scale, light, 0.95).setDepth(-498);
  }

  private drawAmbientBush(x: number, y: number, color: number): void {
    this.add.ellipse(x, y + 4, 26, 9, 0x000000, 0.1).setDepth(-501);
    this.add.ellipse(x - 6, y, 16, 14, color, 0.95).setDepth(-500);
    this.add.ellipse(x + 6, y - 2, 15, 13, color, 0.9).setDepth(-499);
    // 1–2 małe kwiatki (przy ziemi, nie w powietrzu)
    this.add.circle(x - 4, y + 2, 3, 0xf8bbd0, 0.9).setDepth(-498);
    this.add.circle(x + 5, y + 1, 2.5, 0xffe082, 0.85).setDepth(-498);
  }

  private drawDistrictLabels(): void {
    for (const d of TOWN_DISTRICTS) {
      this.add
        .text(d.cx, 28, d.labelPl, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '26px',
          color: '#4a3f55',
          fontStyle: 'bold',
          backgroundColor: '#ffffff99',
          padding: { x: 12, y: 4 },
        })
        .setOrigin(0.5, 0)
        .setDepth(20);
    }
    this.add
      .text(TOWN_PLAYER_START.x, 64, 'Stuknij ziemię · WASD · chodź po dzielnicach', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#6b5f7a',
        backgroundColor: '#ffffff88',
        padding: { x: 8, y: 3 },
      })
      .setOrigin(0.5, 0)
      .setDepth(20);
  }

  private drawGardenGate(): void {
    const { x, y } = TOWN_GARDEN_GATE;
    this.add
      .rectangle(x, y, 100, 140, 0xe8a0bf, 0.55)
      .setStrokeStyle(4, 0xffffff)
      .setDepth(25);
    this.add
      .text(x, y - 10, '🚪', { fontSize: '40px' })
      .setOrigin(0.5)
      .setDepth(26);
    this.add
      .text(x, y + 50, 'Ogród Marzeń', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffcc',
        padding: { x: 6, y: 3 },
      })
      .setOrigin(0.5)
      .setDepth(26);
  }

  private drawColliderHints(colliders: { x: number; y: number; width: number; height: number }[]): void {
    // niewidoczne w prod — lekkie pastele tylko gdy ?debug=1
    if (!/\bdebug=1\b/.test(typeof location !== 'undefined' ? location.search : '')) {
      return;
    }
    for (const c of colliders) {
      this.add
        .rectangle(c.x + c.width / 2, c.y + c.height / 2, c.width, c.height, 0xc4a484, 0.25)
        .setStrokeStyle(1, 0xffffff, 0.4)
        .setDepth(8);
    }
  }

  private drawHudChrome(): void {
    // tytuł fixed do kamery
    this.add
      .text(16, 12, 'Miasteczko Pieskowo', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffbb',
        padding: { x: 10, y: 4 },
      })
      .setScrollFactor(0)
      .setDepth(10_002);
  }

  private buildMinimapLandmarks(): MinimapLandmark[] {
    const lms: MinimapLandmark[] = TOWN_DISTRICTS.map((d) => ({
      id: d.id,
      x: d.cx,
      y: 360,
      color: d.id === 'osiedle' ? 0xffcc99 : d.id === 'rynek' ? 0xffb6c1 : 0x90caf9,
      labelPl: d.labelPl,
    }));
    for (const h of TOWN_BUILDING_HOTSPOTS) {
      lms.push({ id: h.sceneKey, x: h.x, y: h.y, color: h.fill, labelPl: h.labelPl });
    }
    // koty uliczne — łatwiej znaleźć na minimapie
    STREET_CAT_SPAWNS.forEach((c, i) => {
      lms.push({
        id: `street-cat-${i}`,
        x: c.x,
        y: c.y,
        color: 0xff8a65,
        labelPl: 'Kotek',
      });
    });
    return lms;
  }

  /** Oferta misji „Pogłaszcz kotka" gdy QuestLayer ma wolny slot oferty. */
  private offerKotekQuestIfAvailable(): void {
    if (!this.sys.isActive()) return;
    const ql = this.game.scene.getScene(QuestLayer.KEY) as QuestLayer | null;
    if (!ql || typeof ql.offerGiver !== 'function') return;
    ql.offerGiver('kotek');
  }

  /**
   * Budynek = GRAFIKA (bldg_<sceneKey>), origin (0.5,1) na ziemi (W9-A).
   * Brak tekstury → fallback: kolorowa sylwetka domku + emoji (nadal zero-reading).
   */
  private spawnBuildingHotspots(): void {
    for (const h of TOWN_BUILDING_HOTSPOTS) {
      const footDepth = depthForGroundY(h.y);
      const glow = this.add
        .circle(h.x, h.y - 30, 62, 0xfff2b0, 0.0)
        .setStrokeStyle(4, 0xffd166, 0.0)
        .setDepth(footDepth - 1);
      this.buildings.push({ key: h.sceneKey, x: h.x, y: h.y, glow, hotspot: h });

      const bldgKey = `bldg_${h.sceneKey.toLowerCase()}`;
      const w = Math.max(h.width ?? 150, MIN_TOUCH);
      const hgt = Math.max(h.height ?? MIN_TOUCH, MIN_TOUCH);
      let hit: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle;

      if (this.textures.exists(bldgKey)) {
        const src = this.textures.get(bldgKey).getSourceImage() as {
          width: number;
          height: number;
        };
        const place = buildingPlacement(
          h,
          { width: src.width || 200, height: src.height || 200 },
          200,
        );
        hit = this.add
          .image(place.x, place.y, bldgKey)
          .setOrigin(0.5, place.originY)
          .setDisplaySize(place.displayW, place.displayH)
          .setDepth(footDepth);
        // WAVE7: szkoła wyglądała jak kościół — nakładka „szkoła" (tablica + dzwonek)
        if (h.sceneKey === 'School') {
          this.drawSchoolBadge(h.x, h.y - place.displayH * 0.45);
        }
      } else {
        this.drawBuildingSilhouette(h.x, h.y - 48, h.fill);
        this.add
          .text(h.x, h.y - 46, h.icon, { fontSize: '30px' })
          .setOrigin(0.5)
          .setDepth(footDepth + 1);
        hit = this.add
          .rectangle(h.x, h.y - hgt / 2, w, hgt, h.fill, 0.001)
          .setDepth(footDepth);
      }
      hit.setInteractive({ useHandCursor: true });
      attachBuildingHotspotNarration(hit, h);
      hit.on('pointerdown', () => this.onBuildingTap(h));
      hit.setData('navTarget', h.sceneKey);
      hit.setData('hotspot', 'building');
      hit.setName(`hotspot-${h.sceneKey}`);
    }
  }

  /** Tap w budynek: wejście do sceny albo lekka interakcja w miejscu. */
  private onBuildingTap(h: TownHotspot): void {
    if (h.interactionOnly) {
      this.triggerBuildingInteraction(h);
    } else {
      this.enterBuilding(h.sceneKey, h.x, h.y);
    }
  }

  /** Piesek idzie pod budynek, potem wejście (Toca-feel). */
  private enterBuilding(sceneKey: string, x: number, y: number): void {
    if (this.navigating) return;
    this.navigating = true;
    const approach = doorApproachPoint({ x, y });
    this.player?.setTarget(approach.x, approach.y);
    // Krótka pauza: piesek robi krok w stronę drzwi, dopiero potem zmiana sceny
    // (bez opóźnienia setTarget był no-opem — scena znikała natychmiast).
    this.time.delayedCall(360, () => {
      if (!this.sys.isActive()) return;
      getSceneRouter().go(sceneKey);
    });
  }

  /** Budynki bez własnej sceny: podejście + radość + FX + dymek. */
  private triggerBuildingInteraction(h: TownHotspot): void {
    const plan = h.microInteraction
      ? MICRO_INTERACTIONS[h.microInteraction]
      : microInteractionForScene(h.sceneKey);
    const approach = doorApproachPoint(h);
    this.player?.setTarget(approach.x, approach.y);
    this.time.delayedCall(550, () => {
      if (!this.sys.isActive()) return;
      const p = this.player?.position ?? approach;
      if (!plan) {
        playFx(this, 'hearts', p.x, p.y - 30);
        this.showSpeechBubble(p.x, p.y - 70, 'Hau! 🐾');
        return;
      }
      playFx(this, plan.fx, p.x, p.y - 30);
      this.showMicroProp(p.x + 48, p.y - 82, plan);
      this.showSpeechBubble(p.x, p.y - 70, plan.copy);
      void this.playMicroActions(plan.actions);
    });
  }

  private async playMicroActions(actions: readonly TownMicroAction[]): Promise<void> {
    for (const action of actions) {
      if (!this.sys.isActive()) return;
      await this.player?.playAction(action);
    }
  }

  private showMicroProp(
    x: number,
    y: number,
    plan: TownMicroInteractionPlan,
  ): void {
    const prop = this.add
      .text(x, y, plan.propEmoji, { fontSize: '34px' })
      .setOrigin(0.5)
      .setDepth(201)
      .setScale(0.7);
    this.tweens.add({
      targets: prop,
      y: y - 44,
      scale: 1.15,
      angle: 8,
      alpha: 0,
      duration: 1600,
      ease: 'Back.Out',
      onComplete: () => prop.destroy(),
    });
  }

  /** Krótki dymek (reakcja, nie napis budynku) — znika po chwili. */
  private showSpeechBubble(x: number, y: number, text: string): void {
    const bubble = this.add
      .text(x, y, text, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffee',
        padding: { x: 10, y: 6 },
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(200);
    this.tweens.add({
      targets: bubble,
      y: y - 30,
      alpha: 0,
      duration: 1400,
      delay: 700,
      onComplete: () => bubble.destroy(),
    });
  }

  /** Proximity-prompt: rozświetlenie + „Wejdź”. */
  private updateProximity(): void {
    const p = this.player?.position;
    if (!p) return;
    let near: (typeof this.buildings)[number] | null = null;
    let bestD = 130;
    for (const b of this.buildings) {
      const d = Math.hypot(b.x - p.x, b.y - p.y);
      if (d < bestD) {
        bestD = d;
        near = b;
      }
    }
    this.nearKey = near?.key ?? null;
    const t = this.time.now / 1000;
    const pulse = 0.5 + 0.5 * Math.sin(t * 5);
    for (const b of this.buildings) {
      const on = b === near;
      b.glow.setFillStyle(0xfff2b0, on ? 0.18 + 0.14 * pulse : 0);
      b.glow.setStrokeStyle(4, 0xffd166, on ? 0.55 + 0.35 * pulse : 0);
      b.glow.setScale(on ? 1 + 0.05 * pulse : 1);
    }

    if (near) {
      this.showEnterPrompt(near.x, near.y + 48, near.hotspot);
    } else {
      this.hideEnterPrompt();
    }
  }

  /** Bez tekstu: pulsująca strzałka nad budynkiem, gdy piesek jest blisko. Klikalna. */
  private showEnterPrompt(x: number, y: number, h: TownHotspot): void {
    if (this.enterPrompt && this.enterPrompt.getData('key') === h.sceneKey) {
      this.enterPrompt.setPosition(x, y);
      return;
    }
    this.hideEnterPrompt();
    const arrow = this.add
      .triangle(0, 0, 0, 10, 12, -8, -12, -8, 0xffd166)
      .setStrokeStyle(2, 0xffffff);
    const hit = this.add
      .circle(0, 0, 26, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation?.();
      this.onBuildingTap(h);
    });
    this.enterPrompt = this.add.container(x, y, [arrow, hit]);
    this.enterPrompt.setDepth(120);
    this.enterPrompt.setData('key', h.sceneKey);
    this.enterPrompt.setName(`enter-prompt-${h.sceneKey}`);
    this.tweens.add({
      targets: this.enterPrompt,
      y: y - 8,
      duration: 420,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private hideEnterPrompt(): void {
    if (this.enterPrompt) {
      this.tweens.killTweensOf(this.enterPrompt);
      this.enterPrompt.destroy(true);
    }
    this.enterPrompt = null;
  }

  private drawBuildingSilhouette(x: number, y: number, fill: number): void {
    this.add
      .triangle(x, y - 28, 0, 36, 70, 36, 35, 0, fill)
      .setStrokeStyle(2, 0xffffff)
      .setDepth(40);
    this.add
      .rectangle(x, y + 10, 70, 50, fill)
      .setStrokeStyle(2, 0xffffff)
      .setDepth(40);
  }

  /**
   * Nakładka „to jest szkoła" (nie kościół):
   * tablica szkolna, napis Szkoła, książki, flaga ABC.
   */
  private drawSchoolBadge(x: number, y: number): void {
    // tablica szkolna
    this.add
      .rectangle(x, y + 48, 92, 40, 0x2e7d32)
      .setStrokeStyle(4, 0xffffff)
      .setDepth(85);
    this.add
      .text(x, y + 48, 'SZKOŁA', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '13px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(86);
    // flaga ABC
    this.add
      .rectangle(x - 52, y - 78, 36, 22, 0x42a5f5)
      .setStrokeStyle(2, 0xffffff)
      .setDepth(85);
    this.add
      .text(x - 52, y - 78, 'ABC', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '11px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(86);
    // książki przy drzwiach
    this.add.rectangle(x - 40, y + 18, 14, 18, 0xef9a9a).setDepth(84);
    this.add.rectangle(x - 28, y + 14, 12, 22, 0x90caf9).setDepth(84);
    this.add.rectangle(x - 18, y + 16, 12, 20, 0xffe082).setDepth(84);
    // piłka na podwórku (szkoła, nie kościół)
    this.add
      .circle(x + 58, y + 36, 10, 0xff8a65)
      .setStrokeStyle(2, 0xffffff)
      .setDepth(84);
  }

  private onObjectEvent(evt: InteractEvent): void {
    // ławka → sit; ptaszek → odlatuje; kałuża/fontanna = FX w silniku
    if (evt.type === 'tap' || evt.type === 'intention-done') {
      if (evt.defId === 'town_bench') {
        void this.player?.playAction('sit');
      }
      if (evt.defId === 'town_bird') {
        const obj = this.objectLayer?.getObjects().find((o) => o.instanceId === evt.objectId);
        if (obj) {
          this.tweens.add({
            targets: obj.container,
            x: obj.x + 80,
            y: obj.y - 120,
            alpha: 0,
            duration: 600,
            onComplete: () => {
              obj.container.setVisible(false);
            },
          });
        }
      }
    }
  }

  private spawnEstate(): void {
    this.add
      .text(700, 470, 'Osiedle', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(70);

    this.renderEstate(estateSlotsFromHouses(null, TOWN_ESTATE_PLOTS.length));
    void this.loadEstate();
  }

  private async loadEstate(): Promise<void> {
    const res = await listHouses(0);
    if (!this.sys.isActive()) return;
    this.renderEstate(estateSlotsFromHouses(res?.houses ?? null, TOWN_ESTATE_PLOTS.length));
  }

  private renderEstate(slots: EstateSlotVM[]): void {
    this.estateLayer?.destroy(true);
    this.estateLayer = this.add.container(0, 0).setDepth(45);

    TOWN_ESTATE_PLOTS.forEach((plot, i) => {
      const vm = slots[i];
      if (!vm) return;
      const isOwn = vm.isOwn;

      const body = this.add
        .rectangle(plot.x, plot.y - 8, 70, 48, isOwn ? 0xffcc99 : 0xe0d4c8)
        .setStrokeStyle(2, 0xffffff);
      const roof = this.add.triangle(
        plot.x,
        plot.y - 48,
        0,
        28,
        56,
        28,
        28,
        0,
        isOwn ? 0xe07a5f : 0xb0a090,
      );
      this.estateLayer?.add([body, roof]);

      if (isOwn) {
        const btn = UiKit.button(this, {
          x: plot.x,
          y: plot.y + 36,
          label: `${vm.badgePl} ${vm.labelPl}`,
          width: Math.max(120, MIN_TOUCH),
          height: MIN_TOUCH,
          fill: 0xffdab9,
          onClick: () => getSceneRouter().go('House'),
        });
        btn.setDepth(80);
        btn.setData('navTarget', 'House');
        btn.setData('hotspot', 'estate-own');
        btn.setName('hotspot-estate-own');
        this.estateLayer?.add(btn);
        return;
      }

      if (vm.canRequest && vm.houseId) {
        const btn = UiKit.button(this, {
          x: plot.x,
          y: plot.y + 36,
          label: '🤝 Zamieszkaj',
          width: Math.max(150, MIN_TOUCH),
          height: MIN_TOUCH,
          fill: 0xbfe3c0,
          onClick: () => {
            void this.onRequestResidency(vm.houseId as string, btn);
          },
        });
        btn.setDepth(80);
        btn.setData('hotspot', 'estate-request');
        btn.setData('houseId', vm.houseId);
        btn.setName(`hotspot-estate-${vm.houseId}`);
        this.estateLayer?.add(btn);
        return;
      }

      const badge = this.add
        .text(plot.x, plot.y + 36, `${vm.badgePl} ${vm.labelPl}`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '15px',
          color: '#4a3f55',
          fontStyle: 'bold',
          backgroundColor: '#ffffffcc',
          padding: { x: 8, y: 6 },
          align: 'center',
        })
        .setOrigin(0.5)
        .setDepth(80);
      badge.setData('hotspot', `estate-${vm.badge}`);
      badge.setName(`hotspot-estate-${vm.houseId ?? vm.badge + '-' + i}`);
      this.estateLayer?.add(badge);
    });
  }

  private async onRequestResidency(
    houseId: string,
    btn: Phaser.GameObjects.GameObject,
  ): Promise<void> {
    const anyBtn = btn as Phaser.GameObjects.GameObject & {
      disableInteractive?: () => void;
    };
    anyBtn.disableInteractive?.();
    const result = await requestResidency(houseId);
    if (!this.sys.isActive()) return;
    if (result.ok) {
      this.showToast('Wysłano prośbę! 🐾 Poczekaj na odpowiedź.');
    } else if (result.code === 'RATE_LIMIT') {
      this.showToast('Prośba już czeka na odpowiedź. 💛');
    } else {
      this.showToast('Spróbuj za chwilę. 💚');
    }
  }

  private showToast(msg: string): void {
    this.toastText?.destroy();
    this.toastText = this.add
      .text(this.gameW / 2, this.gameH - 60, msg, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#4a3f55',
        backgroundColor: '#fff8f0ee',
        padding: { x: 16, y: 10 },
        align: 'center',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(300);
    this.time.delayedCall(2600, () => {
      this.toastText?.destroy();
      this.toastText = null;
    });
  }

  private teardown(): void {
    this.unsubObjects?.();
    this.unsubObjects = null;
    this.objectLayer?.destroy();
    this.objectLayer = null;
    this.player?.destroy();
    this.player = null;
    this.dogs?.destroy();
    this.dogs = null;
    this.friends?.destroy();
    this.friends = null;
    this.dragon?.destroy();
    this.dragon = null;
    this.streetCats?.destroy();
    this.streetCats = null;
    this.minimap?.destroy();
    this.minimap = null;
    this.edges?.destroy();
    this.edges = null;
    this.worldHandle?.destroy();
    this.worldHandle = null;
    this.estateLayer?.destroy(true);
    this.estateLayer = null;
    this.toastText?.destroy();
    this.toastText = null;
    this.hideEnterPrompt();
    this.buildings = [];
    this.nearKey = null;
    this.navigating = false;
    this.registry.remove('companionFollow');
    this.registry.remove('townDistrict');
  }
}

export { TOWN_BUILDING_HOTSPOTS, townNavigationSceneKeys } from './hotspots.js';
export {
  TOWN_WORLD_W,
  TOWN_WORLD_H,
  TOWN_DISTRICTS,
  districtAtX,
  TOWN_PLAYER_START,
  TOWN_BOUNDS,
} from './hotspots.js';
export { TOWN_OBJECT_DEFS, TOWN_OBJECT_PLACEMENTS, TOWN_MISSING_ASSETS } from './objects.js';
export { buildTownColliders } from './colliders.js';
export { sampleNpcSchedule, MISIA_WAYPOINTS, LOLA_WAYPOINTS } from './npcSchedule.js';
export default TownScene;
